import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ShieldCheck, ShieldAlert, RefreshCw, Copy, Check, ScrollText } from "lucide-react";
import { MfaMethodCard } from "@/components/MfaMethodCard";

const EVENT_LABELS = {
  login_success: "Signed in",
  login_failed: "Failed sign-in",
  login_blocked: "Sign-in blocked",
  login_password_ok: "Password accepted",
  mfa_setup_started: "2FA setup started",
  mfa_setup_failed: "2FA setup code rejected",
  mfa_enabled: "2FA activated",
  mfa_verify_success: "2FA verified",
  mfa_verify_failed: "2FA code rejected",
  mfa_recovery_used: "Backup code used",
  mfa_recovery_failed: "Backup code rejected",
  mfa_recovery_codes_regenerated: "Backup codes re-issued",
  mfa_regen_denied: "Backup code re-issue denied",
  mfa_reset_by_admin: "2FA reset by staff",
  mfa_email_code_sent: "Email code sent",
  mfa_method_changed: "2FA method changed",
  mfa_method_failed: "2FA method change code rejected",
  mfa_method_denied: "2FA method change denied",
};

export default function SecurityPanel({ isStaff = false }) {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [password, setPassword] = useState("");
  const [codes, setCodes] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/mfa/status");
      setStatus(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
    if (isStaff) {
      try {
        const { data } = await api.get("/security/audit-log?limit=60");
        setEvents(data);
      } catch { /* non-fatal */ }
    }
  }, [isStaff]);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setErr(""); setMsg(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/recovery-codes/regenerate", { password });
      setCodes(data.recovery_codes);
      setPassword("");
      setMsg("New backup codes issued. Your previous codes no longer work.");
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    await navigator.clipboard.writeText(codes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="security-panel">
      <div className="lg:col-span-5 space-y-6">
        <section className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)]">
          <div className="px-6 py-4 border-b border-[var(--dojo-border)] flex items-center justify-between">
            <h2 className="font-serif text-2xl">Two-Factor</h2>
            <span className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]" data-testid="mfa-method-label">{status?.method === "email" ? "Email Code" : "Authenticator App"}</span>
          </div>
          <div className="px-6 py-6 space-y-4">
            <div className="flex items-start gap-3" data-testid="mfa-status-badge">
              {status?.enabled ? (
                <ShieldCheck size={20} className="text-[var(--dojo-green)] mt-0.5" />
              ) : (
                <ShieldAlert size={20} className="text-[var(--dojo-hinomaru)] mt-0.5" />
              )}
              <div>
                <div className="font-serif text-xl tracking-tight">
                  {status?.enabled ? "Active" : "Not configured"}
                </div>
                <div className="text-xs text-[var(--dojo-ink-soft)] mt-1">
                  {status?.enabled
                    ? `Enrolled ${status.confirmed_at ? new Date(status.confirmed_at).toLocaleDateString() : ""} · ${status.recovery_codes_remaining} backup codes left`
                    : "You will be asked to enrol the next time you sign in."}
                </div>
              </div>
            </div>
            {status?.required && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--dojo-ink-soft)] border border-[var(--dojo-border)] px-3 py-2 inline-block">
                Mandatory for every dojo account
              </div>
            )}
          </div>
        </section>

        {status?.enabled && <MfaMethodCard status={status} onChanged={load} />}

        {status?.enabled && (
          <section className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)]">
            <div className="px-6 py-4 border-b border-[var(--dojo-border)]">
              <h2 className="font-serif text-2xl">Backup Codes</h2>
            </div>
            <div className="px-6 py-6 space-y-4">
              <p className="text-sm text-[var(--dojo-ink-soft)]">
                Re-issue your ten one-time codes. Confirm with your password — the old set stops working immediately.
              </p>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                data-testid="mfa-regen-password-input"
                className="w-full border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--dojo-ink)]"
              />
              <button
                onClick={regenerate}
                disabled={busy || password.length < 6}
                className="btn-primary flex items-center gap-2"
                data-testid="mfa-regen-submit"
              >
                <RefreshCw size={14} /> {busy ? "Working…" : "Issue new codes"}
              </button>
              {codes.length > 0 && (
                <div className="space-y-3" data-testid="mfa-new-codes">
                  <ul className="grid grid-cols-2 gap-2 font-mono-accent text-xs">
                    {codes.map((c) => (
                      <li key={c} className="border border-[var(--dojo-border)] px-2 py-1.5 tracking-widest text-center">{c}</li>
                    ))}
                  </ul>
                  <button onClick={copyCodes} className="btn-outline flex items-center gap-2 text-xs" data-testid="mfa-copy-new-codes">
                    {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy all"}
                  </button>
                </div>
              )}
              {msg && <div className="text-sm text-[var(--dojo-green)]" data-testid="security-msg">{msg}</div>}
              {err && <div className="text-sm text-[var(--dojo-hinomaru)]" data-testid="security-error">{err}</div>}
            </div>
          </section>
        )}
      </div>

      {isStaff && (
        <div className="lg:col-span-7">
          <section className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)]">
            <div className="px-6 py-4 border-b border-[var(--dojo-border)] flex items-center justify-between">
              <h2 className="font-serif text-2xl flex items-center gap-2"><ScrollText size={16} /> Sign-in Activity</h2>
              <button onClick={load} className="text-[10px] uppercase tracking-[0.2em] text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-ink)]" data-testid="audit-refresh-btn">
                Refresh
              </button>
            </div>
            <div className="divide-y divide-[var(--dojo-border)] max-h-[560px] overflow-y-auto" data-testid="audit-log-list">
              {events.length === 0 && (
                <div className="px-6 py-8 text-sm text-[var(--dojo-ink-soft)]">No events recorded yet.</div>
              )}
              {events.map((e) => (
                <div key={e.id} className="px-6 py-3 grid grid-cols-[1fr_auto] gap-4 items-center" data-testid={`audit-row-${e.id}`}>
                  <div>
                    <div className="text-sm">
                      {EVENT_LABELS[e.event] || e.event}
                      {e.identifier && <span className="text-[var(--dojo-ink-soft)]"> · {e.identifier}</span>}
                    </div>
                    <div className="text-[11px] text-[var(--dojo-ink-soft)] mt-0.5 font-mono-accent">
                      {new Date(e.created_at).toLocaleString()}{e.ip ? ` · ${e.ip}` : ""}{e.detail ? ` · ${e.detail}` : ""}
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border ${
                    e.ok ? "border-[#2E4E3F] text-[#2E4E3F]" : "border-[var(--dojo-hinomaru)] text-[var(--dojo-hinomaru)]"
                  }`}>{e.ok ? "ok" : "denied"}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
